/*
 * <xt-lightbox>: product image lightbox with zoom (feedback 23/09).
 * Opened by Dawn's <modal-opener> through show(opener), like the stock product modal.
 */
if (!customElements.get('xt-lightbox')) {
  class XtLightbox extends HTMLElement {
    constructor() {
      super();
      this.zoomLevel = 2.5;
      this.index = 0;
      this.zoomed = false;
      this.origin = { x: 50, y: 50 };
      this.pointer = null;
    }

    connectedCallback() {
      this.track = this.querySelector('[data-xt-track]');
      this.slides = Array.from(this.querySelectorAll('.xt-lightbox__slide'));
      this.thumbs = Array.from(this.querySelectorAll('[data-xt-goto]'));
      this.counter = this.querySelector('[data-xt-current]');
      this.zoomButton = this.querySelector('[data-xt-zoom]');

      this.stage = this.querySelector('.xt-lightbox__stage');

      // Portal to <body> so no transformed/sticky ancestor can clip the fixed overlay.
      // A re-rendered section brings a fresh copy: drop the older one with the same id.
      if (this.parentElement !== document.body) {
        document.querySelectorAll(`xt-lightbox[id="${this.id}"]`).forEach((el) => {
          if (el !== this && el.parentElement === document.body) el.remove();
        });
        document.body.appendChild(this);
        return;
      }
      if (this.bound) return;
      this.bound = true;

      this.querySelector('[data-xt-close]')?.addEventListener('click', () => this.hide());
      this.querySelector('[data-xt-prev]')?.addEventListener('click', () => this.go(this.index - 1));
      this.querySelector('[data-xt-next]')?.addEventListener('click', () => this.go(this.index + 1));
      this.zoomButton?.addEventListener('click', () => this.setZoom(!this.zoomed));
      this.thumbs.forEach((thumb) => thumb.addEventListener('click', () => this.go(Number(thumb.dataset.xtGoto))));

      this.onKeyDown = this.onKeyDown.bind(this);
      this.stage.addEventListener('pointerdown', this.onPointerDown.bind(this));
      this.stage.addEventListener('pointermove', this.onPointerMove.bind(this));
      this.stage.addEventListener('pointerup', this.onPointerUp.bind(this));
      this.stage.addEventListener('pointercancel', () => this.resetPointer());
      this.stage.addEventListener('mousemove', this.onMouseMove.bind(this));
    }

    show(opener) {
      this.opener = opener;
      const mediaId = opener?.dataset?.mediaId || opener?.closest('[data-media-id]')?.dataset.mediaId;
      const start = Math.max(
        0,
        this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId).split('-').pop())
      );
      this.hidden = false;
      document.body.classList.add('overflow-hidden');
      document.addEventListener('keydown', this.onKeyDown);
      this.go(start, true);
      requestAnimationFrame(() => this.classList.add('is-open'));
      this.querySelector('[data-xt-close]')?.focus({ preventScroll: true });
    }

    hide() {
      this.setZoom(false);
      this.classList.remove('is-open');
      this.hidden = true;
      document.body.classList.remove('overflow-hidden');
      document.removeEventListener('keydown', this.onKeyDown);
      this.opener?.focus({ preventScroll: true });
    }

    go(target, instant) {
      const total = this.slides.length;
      if (!total) return;
      this.setZoom(false);
      this.index = (target + total) % total;
      this.track.style.transition = instant ? 'none' : '';
      this.track.style.transform = `translateX(${-100 * this.index}%)`;
      if (this.counter) this.counter.textContent = String(this.index + 1);
      this.thumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', String(i === this.index)));
      this.thumbs[this.index]?.scrollIntoView({ block: 'nearest', inline: 'center' });
      [this.index - 1, this.index, this.index + 1].forEach((i) => this.load((i + total) % total));
    }

    load(i) {
      const img = this.slides[i]?.querySelector('img[data-src]');
      if (!img) return;
      img.srcset = img.dataset.srcset;
      img.src = img.dataset.src;
      img.removeAttribute('data-src');
    }

    currentImage() {
      return this.slides[this.index]?.querySelector('.xt-lightbox__img');
    }

    setZoom(on, event) {
      const img = this.currentImage();
      const origin = on && img && event ? this.pointToOrigin(img, event.clientX, event.clientY) : { x: 50, y: 50 };
      this.zoomed = Boolean(on) && Boolean(img);
      this.classList.toggle('is-zoomed', this.zoomed);
      this.zoomButton?.setAttribute('aria-pressed', String(this.zoomed));
      this.slides.forEach((slide) => {
        const other = slide.querySelector('.xt-lightbox__img');
        if (other && other !== img) other.style.transform = '';
      });
      if (!img) return;
      if (this.zoomed) {
        this.origin = origin;
        this.applyOrigin(img);
        img.style.transform = `scale(${this.zoomLevel})`;
      } else {
        img.style.transform = '';
      }
    }

    pointToOrigin(img, clientX, clientY) {
      // called before the zoom is applied, so the rect is the unscaled image
      const rect = img.getBoundingClientRect();
      return {
        x: Math.min(100, Math.max(0, ((clientX - rect.left) / rect.width) * 100)),
        y: Math.min(100, Math.max(0, ((clientY - rect.top) / rect.height) * 100)),
      };
    }

    applyOrigin(img) {
      img.style.transformOrigin = `${this.origin.x}% ${this.origin.y}%`;
    }

    onMouseMove(event) {
      if (!this.zoomed || this.pointer || event.sourceCapabilities?.firesTouchEvents) return;
      const img = this.currentImage();
      if (!img) return;
      const rect = this.stage.getBoundingClientRect();
      this.origin = {
        x: Math.min(100, Math.max(0, ((event.clientX - rect.left) / rect.width) * 100)),
        y: Math.min(100, Math.max(0, ((event.clientY - rect.top) / rect.height) * 100)),
      };
      this.applyOrigin(img);
    }

    onPointerDown(event) {
      if (event.button !== undefined && event.button !== 0) return;
      this.pointer = {
        id: event.pointerId,
        type: event.pointerType,
        startX: event.clientX,
        startY: event.clientY,
        lastX: event.clientX,
        lastY: event.clientY,
        moved: false,
        // with pointer capture, pointerup targets the stage: keep what was actually pressed
        target: event.target,
      };
      if (!event.target.closest('button')) this.stage.setPointerCapture?.(event.pointerId);
    }

    onPointerMove(event) {
      const p = this.pointer;
      if (!p || p.id !== event.pointerId) return;
      const dx = event.clientX - p.lastX;
      const dy = event.clientY - p.lastY;
      if (Math.abs(event.clientX - p.startX) > 6 || Math.abs(event.clientY - p.startY) > 6) p.moved = true;
      p.lastX = event.clientX;
      p.lastY = event.clientY;

      if (this.zoomed && p.type !== 'mouse') {
        // drag pans the zoomed image
        const img = this.currentImage();
        const rect = img.getBoundingClientRect();
        this.origin = {
          x: Math.min(100, Math.max(0, this.origin.x - (dx / rect.width) * 100 * (this.zoomLevel / (this.zoomLevel - 1)))),
          y: Math.min(100, Math.max(0, this.origin.y - (dy / rect.height) * 100 * (this.zoomLevel / (this.zoomLevel - 1)))),
        };
        this.applyOrigin(img);
      } else if (!this.zoomed) {
        const offset = event.clientX - p.startX;
        this.track.style.transition = 'none';
        this.track.style.transform = `translateX(calc(${-100 * this.index}% + ${offset}px))`;
      }
    }

    onPointerUp(event) {
      const p = this.pointer;
      if (!p || p.id !== event.pointerId) return;
      const offset = event.clientX - p.startX;
      this.resetPointer();

      if (!p.moved) {
        // a tap / click on the image toggles zoom at that point
        if (p.target.closest('.xt-lightbox__img')) this.setZoom(!this.zoomed, event);
        else if (!p.target.closest('button') && !this.zoomed) this.hide();
        return;
      }
      if (!this.zoomed) {
        this.track.style.transition = '';
        if (Math.abs(offset) > 60) this.go(this.index + (offset < 0 ? 1 : -1));
        else this.track.style.transform = `translateX(${-100 * this.index}%)`;
      }
    }

    resetPointer() {
      if (this.pointer && this.stage.hasPointerCapture?.(this.pointer.id)) this.stage.releasePointerCapture(this.pointer.id);
      this.pointer = null;
    }

    onKeyDown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        if (this.zoomed) this.setZoom(false);
        else this.hide();
      } else if (event.key === 'ArrowLeft') {
        this.go(this.index - 1);
      } else if (event.key === 'ArrowRight') {
        this.go(this.index + 1);
      } else if (event.key === 'Tab') {
        const focusable = Array.from(this.querySelectorAll('button:not([hidden])'));
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }
  }

  customElements.define('xt-lightbox', XtLightbox);
}
